import { registerQuery, useQuery } from 'data-fetching-client';
import http from 'hub-http/clients/apiClient';
import { gate } from 'hub-http/gates';
import { useContext, useMemo } from 'react';

import { useSearchQuery } from '../../../../__generated__/chirp/com/hubspot/video/rpc/HubSpotVideoSearchRpc.hooks';
import { AuthContext } from '../../contexts/AuthContext';

export const VIDEO_CAPTURE_GATE = gate('MediaEditing:CRM:VideoCapture');
export const SHARE_PAGE_V1_GATE = gate('MediaContent:SharePageV1');

export const useIsUngatedForHubSpotVideoCapture = () => {
  const auth = useContext(AuthContext);
  return Boolean(auth && auth.gates.includes(VIDEO_CAPTURE_GATE));
};

export const useIsUngatedForSharePageV1 = () => {
  const auth = useContext(AuthContext);
  return Boolean(auth && auth.gates.includes(SHARE_PAGE_V1_GATE));
};

export const useHasMarketingVideoAccess = () => {
  const auth = useContext(AuthContext);
  return Boolean(auth && auth.user.scopes.includes('marketing-video'));
};

export const useHasOneToOneVideoAccess = () => {
  const auth = useContext(AuthContext);
  return Boolean(auth && auth.user.scopes.includes('one-to-one-video'));
};

export const useHasMediaCaptureAccess = () => {
  const auth = useContext(AuthContext);
  return Boolean(auth && auth.user.scopes.includes('media-capture'));
};

export const useHasHubSpotVideos = (skip = false) => {
  const { data, loading, error } = useSearchQuery({
    variables: { count: 1, offset: 0 },
    skip,
  });

  if (error) {
    // default to true to allow users to open the video picker
    return { hasHubSpotVideos: true, loading: false };
  }

  return {
    hasHubSpotVideos: data ? data.total > 0 : false,
    loading,
  };
};

type Domain = {
  domain: string;
  usageTypes: string[];
  primaryEmailTracking: boolean;
  isUsedForEmailTracking: boolean;
  isAnyPrimary: boolean;
  isUsedForAnyContentType: boolean;
};

const FETCH_DOMAINS = registerQuery({
  fieldName: 'domains',
  fetcher: () => http.get<{ objects: Domain[] }>('/cos-domains/v1/domains'),
});

export const useFetchEmailTrackingDomain = (skip: boolean) => {
  const { data, loading, error } = useQuery(FETCH_DOMAINS, {
    skip,
  });

  const domain = useMemo(() => {
    if (!data) {
      return undefined;
    }

    const portalDomains = data.domains?.objects;
    const primaryEmailTrackingDomain = portalDomains?.find(
      ({ primaryEmailTracking, usageTypes }) =>
        primaryEmailTracking ||
        usageTypes.includes('PRIMARY_FOR_EMAIL_TRACKING')
    );

    if (primaryEmailTrackingDomain) {
      return primaryEmailTrackingDomain.domain;
    } else {
      const anyPrimaryOrUsedDomain = portalDomains?.find(
        ({ isUsedForEmailTracking, isAnyPrimary, isUsedForAnyContentType }) =>
          isUsedForEmailTracking || isAnyPrimary || isUsedForAnyContentType
      );
      return anyPrimaryOrUsedDomain?.domain;
    }
  }, [data]);

  return {
    domain,
    loading,
    error,
  };
};
